import * as React from 'react';
import './progress-uploader.component.css';
import { localization, LocHelperInstance, langChangedName } from '../localization/localization';
import { DebuggerInstance } from '../debugger';
import { Markdown } from '../localization/markdown-wrapper';
import { LogoClearMethod } from '../logo.types';

export enum UploadStatus {
    NotStarted = 0,
    FailTooBig = 1,
    FailExt = 2,
    FailServer = 3,
    Fail = 4,
    Uploading = 5,
    ProcessingMsg = 6,
    Completed = 7
}

export enum UploadMessageKind {
    Message = 'Message',
    Result = 'Result',
    Error = 'Error',
    ProcessingError = 'ProcessingError' // logo je vytvoreno ale pri zpracovani doslo k chybe
}

export interface IUploadMessage<TResponse> {
    kind: UploadMessageKind;
    content: TResponse | null;
    message: string | null;
    progress: number;
}

export interface IProgressUploaderProps<TResponse> {
    onUploadCompleted(sucess: boolean, data: TResponse | null, message: string | null): void;
    onRepeatUpload(): void;
}

export interface IProgressUploaderState {
    uploadStatus: UploadStatus;
    uploadStatusParam: string | null;
    uploadProgress: number;
}

const startTag = '#start#';
const endTag = '#end#';

/**
 * File uploader with status and progress bar
 */
export default class ProgressUploaderComponent<T> extends React.PureComponent<IProgressUploaderProps<T>,IProgressUploaderState> {
    private langUpdateHandle = () => { this.forceUpdate(); }
    
    constructor(props: IProgressUploaderProps<T>) {
        super(props);

        this.state = {
            uploadStatus: UploadStatus.NotStarted,
            uploadStatusParam: '--',
            uploadProgress: 0
        };
    }

    public componentDidMount() {
        LocHelperInstance.langChanged.addListener(langChangedName, this.langUpdateHandle);
    }

    public componentWillUnmount() {
        LocHelperInstance.langChanged.removeListener(langChangedName, this.langUpdateHandle);
    }

    private handleUploadStarted(): void {
        DebuggerInstance.log('upload of file started');

        this.setState({
            uploadStatus: UploadStatus.Uploading,
            uploadStatusParam: null,
            uploadProgress: 0
        });
    }

    private getLastMessage(responseText: string): IUploadMessage<T> | null {
        DebuggerInstance.log('getLastMessage');

        const start = responseText.lastIndexOf(startTag);
        const end = responseText.lastIndexOf(endTag);

        // zadna nebo castecna zprava
        if (start < 0 || end < 0 || start > end) {
            return null;
        }

        const startAfterTag = start + startTag.length;        
        const messageStr = responseText.substr(startAfterTag, end - startAfterTag);
        DebuggerInstance.log(messageStr);
        const message: IUploadMessage<T> = JSON.parse(messageStr);

        return message;
    }

    private handleUploadProgress(event: ProgressEvent): void {
        if (event.lengthComputable) {
            const perc = (event.loaded / event.total) * 100;
            this.setState({
                uploadStatus: UploadStatus.Uploading,
                uploadStatusParam: perc.toString(),
                uploadProgress: perc * 0.2
            });
        } else {
            this.setState({
                uploadStatus: UploadStatus.Uploading,
                uploadStatusParam: null,
                uploadProgress: 20
            });
        }
    }

    private handleUploadCompleted(status: number, responseText: string): void {
        // - posledni prijat zprava
        const lastMessage = this.getLastMessage(responseText);
        let sucess: boolean = false;
        let data: T | null = null;

        if (lastMessage === null || lastMessage.kind === UploadMessageKind.Message) {
            // - zprava je neplatna
            this.setState({
                uploadStatus: UploadStatus.FailServer,
                uploadStatusParam: null,
                uploadProgress: 100
            });
        } else if (lastMessage.kind === UploadMessageKind.Error) {
            // - posledni zprava je chybova
            this.setState({
                uploadStatus: UploadStatus.FailServer,
                uploadStatusParam: lastMessage.message,
                uploadProgress: 100
            });
        } else if (lastMessage.kind === UploadMessageKind.ProcessingError) {
            // - posledni zprava je castecna chyba
            data = lastMessage.content as T;

            this.setState({
                uploadStatus: UploadStatus.FailServer,
                uploadStatusParam: lastMessage.message,
                uploadProgress: 100
            });
        } else if (status >= 200 && status < 300) {
            // - status je ok a posledni zprava je result
            data = lastMessage.content as T;
            sucess = true;

            this.setState({
                uploadStatus: UploadStatus.Completed,
                uploadStatusParam: null,
                uploadProgress: 100
            });
        } else {
            // - posledni zprava je result ale status kod je error
            // - nemelo by nastavat
            data = lastMessage.content as T;

            this.setState({
                uploadStatus: UploadStatus.Fail,
                uploadStatusParam: null,
                uploadProgress: 100
            });
        }

        if (sucess && data !== null) {
            this.props.onUploadCompleted(true, data, null);
        } else {
            // data can still contain logo id for DTP processing
            this.props.onUploadCompleted(false, data, this.getMessageFromStatus());
        }
    }

    public startUpload(file: File, url: string, logoClearMethod: LogoClearMethod) {
        DebuggerInstance.log('startUpload');
        DebuggerInstance.log(this);

        this.handleUploadStarted();

        const formData = new FormData();
        formData.append('logoFile', file, file.name);
        formData.append('clearBackgroundMode', logoClearMethod.toString());

        const request = new XMLHttpRequest();
        request.open('POST', url);
        request.timeout = 120 * 1000; // 2min
        // zobrazovani zprav o zpracovani loga
        request.onreadystatechange = () => {
            DebuggerInstance.log('state change.. state: ' + request.readyState);

            if (request.readyState === 3) {
                const lastMessage = this.getLastMessage(request.responseText);

                if (lastMessage && lastMessage.kind === UploadMessageKind.Message) {
                    DebuggerInstance.log('updating message');
                    DebuggerInstance.log(lastMessage);
                    this.setState({
                        uploadStatus: UploadStatus.ProcessingMsg,
                        uploadStatusParam: lastMessage.message as string,
                        uploadProgress: lastMessage.progress
                    });
                }
            }

            if (request.readyState === 4) {
                DebuggerInstance.log('request.readyState === 4');
                this.handleUploadCompleted(request.status, request.responseText);
            }
        };

        request.upload.onprogress = e => this.handleUploadProgress(e);

        // request.onload = () => {
        //     DebuggerInstance.log('request.onload');
        //     this.handleUploadCompleted(request.status, request.responseText);
        // };
        request.onerror = () => {
            DebuggerInstance.log('request.onerror');
            this.setState({
                uploadStatus: UploadStatus.Fail,
                uploadStatusParam: null,
                uploadProgress: 100
            });
            this.props.onUploadCompleted(false, null, this.getMessageFromStatus());
        }

        request.send(formData);
    }

    private isStatusFail(uploadStatus: UploadStatus): boolean {
        return uploadStatus === UploadStatus.FailTooBig ||
            uploadStatus === UploadStatus.FailExt ||
            uploadStatus === UploadStatus.FailServer ||
            uploadStatus === UploadStatus.Fail;
    }

    private getMessageFromStatus(): string {
        switch (this.state.uploadStatus) {
            case UploadStatus.NotStarted:
                return '--';
            case UploadStatus.FailExt:
                return localization.formatString(
                    localization.uploadLoc.progressUpl.failExtension,
                    this.state.uploadStatusParam as string
                ) as string;
            case UploadStatus.FailTooBig:
                return localization.formatString(
                    localization.uploadLoc.progressUpl.failTooBig,
                    this.state.uploadStatusParam as string
                ) as string;
            case UploadStatus.FailServer:
                if (this.state.uploadStatusParam == null) {
                    return localization.uploadLoc.progressUpl.failServer + '!';
                }
                return localization.uploadLoc.progressUpl.failServer + ': ' + localization.getString(this.state.uploadStatusParam as string, localization.getLanguage());
            case UploadStatus.Fail:
                return localization.uploadLoc.progressUpl.fail;
            case UploadStatus.Uploading:
                if (this.state.uploadStatusParam == null) {
                    return localization.uploadLoc.progressUpl.uploading + ' ...'
                }
                return localization.uploadLoc.progressUpl.uploading + ` ${Math.trunc(+this.state.uploadStatusParam)}%`
            case UploadStatus.ProcessingMsg:
                // return localization.uploadLoc.progressUpl.progress + ' - ' + this.state.uploadStatusParam;
                return localization.uploadLoc.progressUpl.progress + ' - ' + localization.getString(this.state.uploadStatusParam as string, localization.getLanguage());
            case UploadStatus.Completed:
                return localization.uploadLoc.progressUpl.completed + '!';
            default:
                return '--';
        }
    }

    private getProgressBar(hasFailed: boolean): JSX.Element {
        const progress: number = Math.trunc(this.state.uploadProgress);
        const label: string = `${progress}%`;
        const progressStyle: string =  (hasFailed ? 'pu-progress-fail' : 'pu-progress-success');

        return (
            <div className="status-progress-bar">
                <div className="progress">
                    <div className={'progress-bar ' + progressStyle}
                        role="progressbar" 
                        style={{ width: `${progress}%`}} 
                        aria-valuenow={progress} 
                        aria-valuemin={0} 
                        aria-valuemax={100} />
                    <span>{label}</span>
                </div>
            </div>
        )
    }

    private getFailedContent() {
        return (
            <div className="status-failed">
                <button className="main-button primary-style"
                    onClick={() => this.props.onRepeatUpload()}>
                    Try again
                </button>
            </div>
        )
    }

    public render() {
        const hasFailed: boolean = this.isStatusFail(this.state.uploadStatus);
        const statusStyle: string = (hasFailed ? 'status-text-fail' : 'status-text-success');

        const msg: string = this.getMessageFromStatus();

        return (
            <div className="progress-uploader-root">
                <div className="status-text">
                    <span className={statusStyle}><Markdown text={localization.commonLoc.statusTitle} />: <b>{msg}</b></span>
                </div>
                {!hasFailed && this.getProgressBar(hasFailed)}
                {hasFailed && this.getFailedContent()}
            </div>
        );
    }
    
}



// WEBPACK FOOTER //
// ./src/App/shared/components/progress-uploader.component.tsx